'use memo'

import {
  AutopilotTaskDto,
  TaskBlocker,
  TaskBlockerBlockerType,
  TaskCategoryTaskType,
} from '@urbanx/agx.activities.client'
import {
  AgxButton,
  AgxEmptyState,
  AgxSpacer,
  AgxTypography,
} from '@urbanx/agx.ui-components2'
import { ComponentType, JSX, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { LaunchApprovalBlocker } from './LaunchApprovalBlocker'
import MarkedAsExchange from './BlockerModals/MarkedAsExchange/MarkedAsExchange'
import { useMarkedAsExchange } from './BlockerModals/MarkedAsExchange/useMarkedAsExchange'

interface BlockerLayoutProps {
  task: AutopilotTaskDto
  title?: string
  description?: string
  children?: ReactNode
}

interface BlockerDisplayItem {
  title?: string
  description?: string
  buttonText: string
  taskLink?: string
  buttonAction?: () => void
  modalComponent?: ComponentType<BlockerModalProps>
  rowComponent?: ComponentType<BlockerRowProps>
  blocker: TaskBlocker
}

interface BlockerModalProps {
  controller: ReturnType<typeof useMarkedAsExchange>
}

const blockerCopyByType: Record<
  TaskBlockerBlockerType,
  Omit<BlockerDisplayItem, 'taskLink' | 'blocker'>
> = {
  [TaskBlockerBlockerType.InitialDepositTask]: {
    title: 'Initial Deposit Outstanding.',
    description: 'Pay the Initial Deposit to progress the task',
    buttonText: 'Go to Deposits',
  },
  [TaskBlockerBlockerType.BalanceDepositTask]: {
    title: 'Balance Deposit Outstanding.',
    description: 'Pay the Balance Deposit to progress the task',
    buttonText: 'Go to Deposits',
  },
  [TaskBlockerBlockerType.ContractUploadedStatusDraft]: {
    title: 'Contract Uploaded Status Draft',
    description: 'Awaiting Final Contract from Solicitor',
    buttonText: 'Upload Final Contract',
  },
  [TaskBlockerBlockerType.CommissionShortfallConfirmation]: {
    title: 'Commission Shortfall Confirmation',
    description: 'Update the Commission Type to progress the task',
    buttonText: 'Go to Commission',
  },
  [TaskBlockerBlockerType.ReferralInvoiceOutstanding]: {
    title: 'Referral / Conjunction Invoice Outstanding',
    description:
      'Complete the Referral / Conjunction Invoice task to progress Settlement Payments',
    buttonText: 'Go to Referral / Conjunction',
  },
  [TaskBlockerBlockerType.CommissionPlanTypeVendorChoice]: {
    title: 'Commission is Vendor Choice',
    description: 'Update copy for Commission Plan Type Vendor Choice blocker',
    buttonText: 'Go to Commission',
  },
  [TaskBlockerBlockerType.LaunchDateNotSet]: {
    title: 'Launch Date Not Set',
    description: 'Update the Launch Date to progress the task',
    buttonText: 'Go to Launch Details',
  },
  [TaskBlockerBlockerType.ApprovalReceivedNotConfirmed]: {
    title: 'Awaiting Agent Approval',
    description:
      'Once approval is received, you can confirm it to progress the launch task.',
    buttonText: 'Confirm Approval',
    rowComponent: LaunchApprovalBlockerRow,
  },
  [TaskBlockerBlockerType.MarkedAsExchanged]: {
    title: 'Pending Confirmation of Exchange',
    buttonText: 'Mark as Exchanged',
    modalComponent: MarkedAsExchange,
    rowComponent: MarkedAsExchangeBlockerRow,
  },
  [TaskBlockerBlockerType.TerminationTaskBuyerBankDetails]: {
    title: 'Buyer Bank Details Outstanding',
    description:
      'Once Buyer Bank Details are complete, you can complete Settlement Payments',
    buttonText: 'Go to Finance',
  },
  [TaskBlockerBlockerType.TerminationTaskVendorBankDetails]: {
    title: 'Vendor Bank Details Outstanding',
    description:
      'Once Vendor Bank Details are complete, you can complete Settlement Payments',
    buttonText: 'Go to Finance',
  },
  [TaskBlockerBlockerType.WithdrawnTaskVendorBankDetails]: {
    title: 'Vendor Bank Details Outstanding',
    description:
      'Once Vendor Bank Details are complete, you can complete Settlement Payments',
    buttonText: 'Go to Tasks',
  },
  [TaskBlockerBlockerType.VendorBankDetailsOutstanding]: {
    title: 'Vendor Bank Details Outstanding',
    description:
      'Complete the Vendor Bank Details task to progress Settlement Payments',
    buttonText: 'Go to Vendor Details',
  },
  [TaskBlockerBlockerType.NotMarkedAsSettled]: {
    title: 'Pending Settlement Confirmation',
    buttonText: 'Mark as Settled',
  },
  [TaskBlockerBlockerType.ContractDateNotSet]: {
    title: 'NOT IMPLEMENTED',
    description: 'NOT IMPLEMENTED',
    buttonText: 'NOT IMPLEMENTED',
  },
}

export const BlockerLayout = ({
  task,
  title,
  description,
  children,
}: BlockerLayoutProps): JSX.Element => {
  const blockerItems = (task.blockers ?? [])
    .map((blocker) => mapBlockerToDisplayItem(task, blocker))
    .filter(isBlockerDisplayItem)

  const shouldRenderDefaultBlockerContent = children == null
  const showFallbackContent =
    blockerItems.length === 0 && (title || description)
  return (
    <AgxSpacer
      fill
      direction="vertical"
      contentJustification="center"
      contentAlignment="center"
      gap="lg"
    >
      <AgxSpacer
        direction="vertical"
        fill
        contentAlignment="center"
        gap="lg"
        padding={{ left: 'spacing-8xl', right: 'spacing-8xl' }}
      >
        {children}
        {shouldRenderDefaultBlockerContent && showFallbackContent && (
          <AgxSpacer direction="vertical" contentAlignment="center" gap="sm">
            {title && (
              <AgxTypography
                id="pendingTitle"
                type="heading"
                size="small"
                color="var(--text-primary)"
              >
                {title}
              </AgxTypography>
            )}
            {description && (
              <AgxTypography
                id="pendingDescription"
                type="body_ui"
                size="small"
                color="var(--text-secondary)"
              >
                {description}
              </AgxTypography>
            )}
          </AgxSpacer>
        )}
        {shouldRenderDefaultBlockerContent && task.assignedTo == null && (
          <AgxSpacer>
            <AgxEmptyState
              variant="advanced"
              label="Assign task to unlock"
              glyphType={'locked'}
              actionButtons={[]}
            ></AgxEmptyState>
          </AgxSpacer>
        )}
        {shouldRenderDefaultBlockerContent && task.assignedTo != null && (
          <AgxSpacer
            direction="vertical"
            fill
            contentAlignment="center"
            gap="lg"
          >
            {blockerItems.map((item, index) => {
              const RowComponent = item.rowComponent ?? BlockerRow

              return (
                <RowComponent
                  key={`blocker-row-${index}`}
                  item={item}
                  task={task}
                />
              )
            })}
          </AgxSpacer>
        )}
      </AgxSpacer>
    </AgxSpacer>
  )
}

interface BlockerRowProps {
  item: BlockerDisplayItem
  task: AutopilotTaskDto
}

const BlockerRow = ({ item }: BlockerRowProps) => {
  const navigate = useNavigate()
  const taskLink = item.taskLink

  return (
    <AgxSpacer direction="vertical" contentAlignment="center" gap="sm" fill>
      {item.title && (
        <AgxTypography
          id="pendingTitle"
          type="heading"
          size="large"
          color="var(--text-primary)"
        >
          {item.title}
        </AgxTypography>
      )}

      {item.description && (
        <AgxTypography
          id="pendingDescription"
          type="body_ui"
          size="small"
          color="var(--text-secondary)"
        >
          {item.description}
        </AgxTypography>
      )}

      {item.buttonText ? (
        <AgxButton
          id="pendingTaskButton"
          text={item.buttonText}
          variant="primary"
          onClick={() =>
            taskLink ? navigate(taskLink) : item.buttonAction?.()
          }
        />
      ) : null}
    </AgxSpacer>
  )
}

function MarkedAsExchangeBlockerRow({ item, task }: BlockerRowProps) {
  const controller = useMarkedAsExchange({ task })
  const ModalComponent = item.modalComponent
  const itemWithAction = {
    ...item,
    buttonAction: controller.openModal,
  }

  return (
    <>
      <BlockerRow item={itemWithAction} task={task} />
      {ModalComponent ? <ModalComponent controller={controller} /> : null}
    </>
  )
}

function LaunchApprovalBlockerRow({ task }: BlockerRowProps) {
  if (!task.campaignId) return null

  return <LaunchApprovalBlocker campaignId={task.campaignId} />
}

const mapBlockerToDisplayItem = (
  task: AutopilotTaskDto,
  blocker: TaskBlocker
): BlockerDisplayItem | null => {
  const blockerType = blocker.blockerType
  if (!blockerType || !(blockerType in blockerCopyByType)) {
    return null
  }

  const mappedCopy = blockerCopyByType[blockerType]
  const markedAsExchangedOverrides =
    blockerType === TaskBlockerBlockerType.MarkedAsExchanged &&
    task.category?.taskType === TaskCategoryTaskType.AgentExchangeInformation
      ? {
          title: 'Awaiting Final Contract from Solicitor',
          buttonText: 'Upload Final Contract',
        }
      : undefined

  return {
    title: markedAsExchangedOverrides?.title ?? mappedCopy.title,
    description: mappedCopy.description,
    buttonText: markedAsExchangedOverrides?.buttonText ?? mappedCopy.buttonText,
    taskLink: blocker.taskLink,
    buttonAction: mappedCopy.buttonAction,
    modalComponent: mappedCopy.modalComponent,
    rowComponent: mappedCopy.rowComponent,
    blocker,
  }
}

const isBlockerDisplayItem = (
  item: BlockerDisplayItem | null
): item is BlockerDisplayItem => item !== null
